import React, {useState, useEffect} from 'react'
import {Typography, Box, Chip, Divider, IconButton} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'

import withLayout from '../../hocs/Layout'
import MerchantDetails from '../merchant-onboarding/details'
import MerchantKYC from '../merchant-onboarding/kyc'
import BankDetails from '../merchant-onboarding/details/bankDetails'
import PickupLocationTab from '../merchant-onboarding/locations/pickupLocation'
import {post, apiConstants, getGeneric} from '../../common/constants'
import {getMerchant} from '../../helpers/ReduxHelpers'
import PageTitle from '../../common/PageTitle'
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography'
import {NAVY, BORDER, LIGHT_BG, SUBTLE, WHITE, withOpacity} from '../../styles/foundation/colors'
import {SHADOW} from '../../styles/foundation/effects'

const pageSx = {fontFamily: FONT, p: {xs: 1.5, md: 2.5}, bgcolor: LIGHT_BG, minHeight: '100vh'}

const heroSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '14px', boxShadow: SHADOW.card,
  p: {xs: 2, md: 2.5}, mb: 2.5,
  display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap',
}

const cardSx = {bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '14px', boxShadow: SHADOW.card}

const STATUS_MAP = {
  KYC_PENDING: {label: 'KYC Pending', bg: '#FFF3E0', text: '#E65100'},
  KYC_UPLOADED: {label: 'KYC Uploaded', bg: '#E3F2FD', text: '#1565C0'},
  KYC_APPROVED: {label: 'KYC Approved', bg: '#E8F5E9', text: '#2E7D32'},
}

const SECTIONS = [
  {key: 'profile', title: 'Company Profile', sub: 'Account & contact details', icon: <PersonOutlineIcon />, accent: '#3783e7'},
  {key: 'bank', title: 'Bank Details', sub: 'Payout account', icon: <AccountBalanceOutlinedIcon />, accent: '#06d6a0'},
  {key: 'kyc', title: 'KYC Verification', sub: 'Identity & documents', icon: <VerifiedUserOutlinedIcon />, accent: '#E65100'},
  {key: 'pickup', title: 'Pickup Locations', sub: 'Where we collect orders', icon: <LocationOnOutlinedIcon />, accent: '#283593'},
  {key: 'channel', title: 'Default Channel', sub: 'Order source', icon: <StorefrontOutlinedIcon />, accent: '#637381'},
]

function DefaultChannel({merchantId}) {
  const [defaultChannel, setDefaultChannel] = useState(null)
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    if (!merchantId) return
    setLoading(true)
    getGeneric(`${apiConstants.MERCHANT_DETAILS}/${merchantId}`)
      .then((res) => { setDefaultChannel(res?.data?.channelNameDefault || null); setLoading(false) })
      .catch(() => setLoading(false))
  }, [merchantId])
  return (
    <Box sx={{bgcolor: LIGHT_BG, border: `1px solid ${BORDER}`, borderRadius: '10px', p: '14px 16px', maxWidth: 420}}>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.03em'}}>
        Default Channel
      </Typography>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.semibold, color: loading || !defaultChannel ? SUBTLE : NAVY}}>
        {loading ? 'Loading…' : defaultChannel || 'No default channel set'}
      </Typography>
    </Box>
  )
}

function Profile() {
  const merchant = getMerchant()
  const merchantId = merchant?.id || ''
  const [reload, setReload] = useState(0)
  const [merchantData, setMerchantData] = useState()
  const [merchantStatus, setMerchantStatus] = useState('KYC_PENDING')
  const [active, setActive] = useState('profile')

  // Scroll arrows for the tab strip — shown only when there's more to scroll to on
  // that side, since the native scrollbar is hidden. Mirrors the Channel Orders tabs.
  const tabScrollRef = React.useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollArrows = React.useCallback(() => {
    const el = tabScrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 2)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2)
  }, [])

  useEffect(() => {
    updateScrollArrows()
    window.addEventListener('resize', updateScrollArrows)
    return () => window.removeEventListener('resize', updateScrollArrows)
  }, [updateScrollArrows])

  const scrollTabsBy = (amount) => {
    tabScrollRef.current?.scrollBy({left: amount, behavior: 'smooth'})
  }

  useEffect(() => {
    if (merchantId) getData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, merchantId])

  const getData = async () => {
    try {
      const url = apiConstants.BASE_LOCAL + apiConstants.GET_MERCHANT_SEARCH
      const {data} = await post(url, {
        offset: 0, limit: 1, orfilter: [],
        andfilter: [{field: 'id', operator: 'eq', value: merchantId}],
        excludes: 'mappedOrderRule,sla,codRemittanceConfig',
      })
      setMerchantData(data)
      setMerchantStatus(data?.data?.[0]?.status)
    } catch (e) {
      console.error('Profile fetch error:', e)
    }
  }

  const status = STATUS_MAP[merchantStatus] || STATUS_MAP.KYC_PENDING
  const name = merchant?.name || 'Merchant'
  const rawEmail = merchant?.email || merchantData?.data?.[0]?.email || ''
  const rawContact = merchant?.contact_no || merchantData?.data?.[0]?.contact_no || ''
  // Don't surface PII-masked values (e.g. "*****") in the hero.
  const email = rawEmail.includes('*') ? '' : rawEmail
  const contact = String(rawContact).includes('*') ? '' : rawContact
  const initials = name.trim().split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase()
  const activeSection = SECTIONS.find((s) => s.key === active) || SECTIONS[0]

  return (
    <Box sx={pageSx}>
      <PageTitle>Profile & KYC</PageTitle>

      {/* Hero — merchant identity */}
      <Box sx={heroSx}>
        <Box sx={{
          width: 64, height: 64, borderRadius: '16px', flexShrink: 0, zIndex: 1,
          background: 'linear-gradient(135deg, #3783e7, #7c4dff)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: FONT, fontWeight: WEIGHT.extra, fontSize: '1.4rem', color: WHITE, boxShadow: '0 6px 18px rgba(55,131,231,0.4)',
        }}>
          {initials}
        </Box>
        <Box sx={{flex: 1, minWidth: 200, zIndex: 1}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
            <Typography sx={{color: NAVY, fontWeight: WEIGHT.bold, fontSize: '1.35rem', fontFamily: FONT}}>{name}</Typography>
            <Chip size='small' label={status.label} sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: '0.7rem', bgcolor: status.bg, color: status.text, height: 24}} />
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2.5, mt: 0.8, flexWrap: 'wrap'}}>
            {email && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.6}}>
                <EmailOutlinedIcon sx={{fontSize: 15, color: SUBTLE}} />
                <Typography sx={{color: SUBTLE, fontSize: '0.82rem', fontFamily: FONT}}>{email}</Typography>
              </Box>
            )}
            {contact && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.6}}>
                <PhoneOutlinedIcon sx={{fontSize: 15, color: SUBTLE}} />
                <Typography sx={{color: SUBTLE, fontSize: '0.82rem', fontFamily: FONT}}>{contact}</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Section tabs — segmented card, mirrors the Channel Orders tab strip */}
      <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2.5, overflow: 'hidden', position: 'relative'}}>
        {canScrollLeft && (
          <IconButton onClick={() => scrollTabsBy(-220)} size='small'
            sx={{
              position: 'absolute', left: 0, top: 0, bottom: 0, zIndex: 2, borderRadius: 0, px: 0.5, bgcolor: WHITE,
              background: `linear-gradient(to right, ${WHITE} 60%, ${withOpacity(WHITE, 0)})`,
              '&:hover': {bgcolor: 'transparent'},
            }}>
            <ChevronLeftIcon sx={{color: SUBTLE}} />
          </IconButton>
        )}
        {canScrollRight && (
          <IconButton onClick={() => scrollTabsBy(220)} size='small'
            sx={{
              position: 'absolute', right: 0, top: 0, bottom: 0, zIndex: 2, borderRadius: 0, px: 0.5, bgcolor: WHITE,
              background: `linear-gradient(to left, ${WHITE} 60%, ${withOpacity(WHITE, 0)})`,
              '&:hover': {bgcolor: 'transparent'},
            }}>
            <ChevronRightIcon sx={{color: SUBTLE}} />
          </IconButton>
        )}
        <Box ref={tabScrollRef} onScroll={updateScrollArrows}
          sx={{
            display: 'flex', overflowX: 'auto',
            scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': {display: 'none'},
          }}>
          {SECTIONS.map((sec, i) => {
            const isActive = sec.key === active
            return (
              <React.Fragment key={sec.key}>
                {i > 0 && <Divider orientation='vertical' flexItem sx={{borderColor: BORDER}} />}
                <Box onClick={() => setActive(sec.key)}
                  sx={{
                    flex: '1 1 auto', minWidth: 170, py: 1.25, px: 2, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                    borderBottom: isActive ? `3px solid ${sec.accent}` : '3px solid transparent',
                    bgcolor: isActive ? withOpacity(sec.accent, 0.06) : 'transparent',
                    transition: 'all 0.15s',
                    '&:hover': {bgcolor: withOpacity(sec.accent, 0.04)},
                  }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                    bgcolor: isActive ? withOpacity(sec.accent, 0.12) : withOpacity(sec.accent, 0.07),
                    color: isActive ? sec.accent : SUBTLE,
                    '& .MuiSvgIcon-root': {fontSize: 17},
                  }}>
                    {sec.icon}
                  </Box>
                  <Typography sx={{
                    fontFamily: FONT, fontSize: SIZE.xs, fontWeight: isActive ? WEIGHT.bold : WEIGHT.medium,
                    color: isActive ? NAVY : SUBTLE, lineHeight: 1.2, whiteSpace: 'nowrap',
                  }}>
                    {sec.title}
                  </Typography>
                </Box>
              </React.Fragment>
            )
          })}
        </Box>
      </Box>

      {/* Full-width content */}
      <Box sx={cardSx}>
        {/* Section header */}
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '10px', bgcolor: `${activeSection.accent}14`, color: activeSection.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': {fontSize: 20},
          }}>{activeSection.icon}</Box>
          <Box>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY}}>{activeSection.title}</Typography>
            <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
              {active === 'kyc' && merchantStatus === 'KYC_PENDING' ? 'Verification may take 2–3 business days' : activeSection.sub}
            </Typography>
          </Box>
        </Box>

        {/* Section content */}
        <Box sx={{p: {xs: 2, md: 2.5}}}>
          {active === 'profile' && <MerchantDetails />}
          {active === 'bank' && <BankDetails />}
          {active === 'kyc' && <MerchantKYC setReload={setReload} merchantData={merchantData?.data?.[0]} merchantStatus={merchantStatus} />}
          {active === 'pickup' && <PickupLocationTab />}
          {active === 'channel' && <DefaultChannel merchantId={merchantId} />}
        </Box>
      </Box>
    </Box>
  )
}

export default withLayout(Profile)
